import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Coins, Trophy, Clock, ChevronDown, Flag, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const CYAN = "#2dd4bf";
const TZ = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" })
  .formatToParts(new Date()).find((p) => p.type === "timeZoneName")?.value || "";

function Section({ title, children, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="border border-race-line rounded-md overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} data-testid={`summary-section-${title}`}
        className="w-full flex items-center justify-between px-4 py-3 bg-race-surface hover:bg-race-elevated transition-colors">
        <span className="overline">{title}</span>
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 py-3 text-sm text-race-sub space-y-2">{children}</div>}
    </div>
  );
}

// Public event view for a scanned QR when the visitor is NOT yet a member.
export default function ContestSummary({ id, refCode = "" }) {
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/contests/${id}`).then((r) => setC(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <div className="text-race-sub p-10 text-center">Contest not found.</div>;
  if (!c) return <div className="text-race-sub p-10 text-center">Loading event…</div>;

  const prize = c.prize_pool_credits > 0 ? `$${(c.prize_pool_credits / 100).toFixed(0)}` : "-";
  const fee = c.entry_fee_credits > 0 ? `${c.entry_fee_credits} credits` : "Free";
  const joinRace = () => navigate(
    `/register?next=${encodeURIComponent(`/e/${id}`)}${refCode ? `&ref=${encodeURIComponent(refCode)}` : ""}`);

  return (
    <div className="max-w-lg mx-auto animate-slide-up" data-testid="contest-summary-page">
      <div className="flex items-center gap-2 mb-2" style={{ color: CYAN }}>
        <Flag size={16} /><span className="overline">Contest Summary · IBRA Sanctioned</span>
      </div>
      <h1 className="font-heading text-4xl sm:text-5xl tracking-tight">{c.name}</h1>
      <p className="text-race-sub mt-1 capitalize">
        {c.race_type} · {dayjs(c.start_time).format("ddd, MMM D · h:mm A")} {TZ}
      </p>

      <div className="grid grid-cols-2 gap-3 mt-5">
        <div className="bg-race-surface border border-race-line rounded-md p-4">
          <div className="flex items-center gap-2 text-race-sub text-xs"><Trophy size={14} /> PRIZE</div>
          <div className="font-heading text-3xl tracking-tight mt-1" style={{ color: CYAN }}>{prize}</div>
        </div>
        <div className="bg-race-surface border border-race-line rounded-md p-4">
          <div className="flex items-center gap-2 text-race-sub text-xs"><Coins size={14} /> ENTRY</div>
          <div className="font-heading text-3xl tracking-tight mt-1 text-race-txt">{fee}</div>
        </div>
      </div>

      <div className="space-y-2 mt-5">
        <Section title="Details" defaultOpen>
          <div className="flex items-center gap-2"><Clock size={14} /> Starts {dayjs(c.start_time).format("dddd, MMMM D, h:mm A")} {TZ}</div>
          <div>Format: <span className="capitalize">{c.race_type}</span> · {c.length_chars} characters · {c.transmission} keys</div>
          {c.difficulty && <div>Difficulty: <span className="capitalize">{c.difficulty}</span></div>}
        </Section>
        <Section title="Rules">
          <ul className="list-disc list-inside space-y-1">
            <li>No delete key - mistakes can't be corrected.</li>
            <li>Each incorrect keystroke adds {c.penalty_seconds}s to your time.</li>
            <li>Registration stays open until the race starts.</li>
            <li>Server-verified results with anti-cheat enforcement.</li>
          </ul>
        </Section>
        <Section title="Eligibility">
          <div className="flex items-center gap-2"><ShieldCheck size={14} /> Requires an active BR License in good standing.</div>
          {(c.entry_fee_credits > 0 || c.prize_pool_credits > 0) && <div>Real-money contest - geo/region restrictions may apply.</div>}
        </Section>
      </div>

      <button onClick={joinRace} data-testid="summary-register-btn"
        className="w-full mt-6 font-heading text-2xl tracking-wide py-4 rounded-sm shadow-lg hover:scale-[1.02] transition-transform"
        style={{ background: CYAN, color: "#04120f" }}>
        REGISTER TO RACE
      </button>
      <p className="text-center text-race-sub text-xs mt-3">
        You'll create your free racing account and activate a BR License, then land right back here to enter.
      </p>
    </div>
  );
}
